import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock, History, Search, Send, Users } from 'lucide-react';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Skeleton } from '../../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { formatDate, scoreTone } from '../qualityConfig';
import { EngagementBar } from './EngagementBar';

const SHOW = [
    { id: 'all', label: 'All recruiters', test: () => true },
    { id: 'attention', label: 'Needs attention', test: (u) => u.not_opened > 0 || u.due_soon > 0 },
    { id: 'not_opened', label: 'Has unopened tasks', test: (u) => u.not_opened > 0 },
    { id: 'in_progress', label: 'Has tasks in progress', test: (u) => u.in_progress > 0 },
    { id: 'missed', label: 'Has missed tasks', test: (u) => u.missed > 0 },
    { id: 'none', label: 'No tasks yet', test: (u) => !u.total },
];

const filterTeam = (team, query, dept, show) => {
    const needle = query.trim().toLowerCase();
    const test = SHOW.find((s) => s.id === show).test;
    return team.filter((u) => test(u) && (dept === 'all' || (u.department || 'none') === dept)
        && (!needle || `${u.name} ${u.email} ${u.department || ''}`.toLowerCase().includes(needle)));
};

export const AdminTag = () => (
    <span className="ml-1.5 rounded-full border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-indigo-700 dark:border-indigo-800/60 dark:bg-indigo-950/60 dark:text-indigo-300">Admin</span>
);

const DueChip = ({ n }) => (n > 0
    ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300"><AlarmClock className="h-3 w-3" />{n}</span>
    : <span className="text-muted-foreground">—</span>);

const MemberRow = ({ u, checked, onCheck, onAssign, onHistory, index }) => (
    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(index, 12) * 0.02 }}
        className="border-b transition-colors hover:bg-muted/40" data-testid={`team-row-${u.id}`}>
        <TableCell className="w-10"><Checkbox checked={checked} onCheckedChange={() => onCheck(u.id)} data-testid={`team-check-${u.id}`} /></TableCell>
        <TableCell>
            <button type="button" onClick={() => onHistory(u)} className="flex items-center gap-3 text-left" data-testid={`team-name-${u.id}`}>
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-violet-500/15 text-xs font-semibold text-violet-700 dark:text-violet-300">{u.name?.charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                <div className="min-w-0"><p className="truncate font-medium hover:underline">{u.name}{u.role === 'admin' && <AdminTag />}</p><p className="truncate text-xs text-muted-foreground">{u.email}</p></div>
            </button>
        </TableCell>
        <TableCell className="text-sm">{u.department || <span className="text-muted-foreground">—</span>}</TableCell>
        <TableCell><EngagementBar u={u} /></TableCell>
        <TableCell data-testid={`team-due-${u.id}`}><DueChip n={u.due_soon} /></TableCell>
        <TableCell className={`font-heading text-base font-bold tabular-nums ${scoreTone(u.avg_score)}`} data-testid={`team-avg-${u.id}`}>{u.avg_score ?? '—'}</TableCell>
        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(u.last_assigned_at)}</TableCell>
        <TableCell className="text-right">
            <div className="flex justify-end gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => onHistory(u)} aria-label="Task history" data-testid={`team-history-${u.id}`}><History className="h-4 w-4" /></Button>
                <Button size="sm" onClick={() => onAssign([u.id])} className="bg-violet-600 text-white hover:bg-violet-700" data-testid={`team-assign-${u.id}`}>
                    <Send className="mr-1.5 h-3.5 w-3.5" /> Assign task
                </Button>
            </div>
        </TableCell>
    </motion.tr>
);

const Toolbar = ({ query, setQuery, dept, setDept, departments, show, setShow, checked, onAssign }) => (
    <div className="flex flex-wrap items-center gap-3 border-b p-4">
        <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search recruiters..." className="pl-9" data-testid="team-search" />
        </div>
        <Select value={show} onValueChange={setShow}>
            <SelectTrigger className="w-full sm:w-52" data-testid="team-show-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{SHOW.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="w-full sm:w-48" data-testid="team-department-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d === 'none' ? 'No department' : d}</SelectItem>)}
            </SelectContent>
        </Select>
        {checked.length > 0 && (
            <Button onClick={() => onAssign(checked)} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="team-assign-selected">
                <Users className="mr-2 h-4 w-4" /> Assign to {checked.length} selected
            </Button>
        )}
    </div>
);

export const TeamTab = ({ team, loading, onAssign, onHistory }) => {
    const [query, setQuery] = useState('');
    const [dept, setDept] = useState('all');
    const [show, setShow] = useState('all');
    const [checked, setChecked] = useState([]);
    const departments = useMemo(() => [...new Set(team.map((u) => u.department || 'none'))].sort(), [team]);
    const rows = useMemo(() => filterTeam(team, query, dept, show), [team, query, dept, show]);
    const toggle = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
    const allChecked = rows.length > 0 && rows.every((u) => checked.includes(u.id));

    return (
        <Card className="overflow-hidden" data-testid="quality-team-tab">
            <Toolbar query={query} setQuery={setQuery} dept={dept} setDept={setDept} departments={departments} show={show} setShow={setShow} checked={checked} onAssign={onAssign} />
            {loading ? <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={() => setChecked(allChecked ? [] : rows.map((u) => u.id))} data-testid="team-check-all" /></TableHead>
                            <TableHead>Recruiter</TableHead><TableHead>Department</TableHead><TableHead>Task tracking</TableHead><TableHead>Due &lt; 48h</TableHead>
                            <TableHead>Avg score</TableHead><TableHead>Last assigned</TableHead><TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((u, i) => <MemberRow key={u.id} u={u} index={i} checked={checked.includes(u.id)} onCheck={toggle} onAssign={onAssign} onHistory={onHistory} />)}
                        {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground" data-testid="team-empty">No recruiters match your filters</TableCell></TableRow>}
                    </TableBody>
                </Table>
            )}
        </Card>
    );
};
